import React, { useState, useEffect } from 'react';
import { Checkbox, Radio, RadioGroup, Field, Label, Input } from '@headlessui/react';
import clsx from 'clsx';

const options = [
  { name: 'People', subheadline: 'Für Chancengerechtigkeit', desc: 'Wir suchen nach jungen Menschen, die sich dafür einsetzen, dass alle Stimmen in der Gesellschaft gehört werden und alle eine faire Chance bekommen.' },
  { name: 'Society', subheadline: 'Für offene Gesellschaften', desc: 'Wir suchen nach jungen Menschen, die dazu beitragen, dass unsere Demokratie und Gemeinschaft stark bleibt.' },
  { name: 'Planet', subheadline: 'Für einen lebendigen Planeten', desc: 'Wir suchen nach jungen Menschen, die sich für Klimagerechtigkeit und für Umweltschutz einsetzen.' },
  { name: 'Other', subheadline: 'In keinem der drei Bereiche', desc: '' }
];

const Step1 = ({ next, abort, data, triggerDialog, setResetOption }) => {
  const [selectedOption, setSelectedOption] = useState(data.selectedOption || '');
  const [checkboxes, setCheckboxes] = useState({
    age: data.age || false,
    inGermany: data.inGermany || false,
    nomineeKnows: data.nomineeKnows || false,
  });
  const [errors, setErrors] = useState({});
  const [dialogTriggered, setDialogTriggered] = useState(false);

  useEffect(() => {
    if (selectedOption === 'Other') {
      setDialogTriggered(true);
      triggerDialog();
    }
  }, [selectedOption, triggerDialog]);

  useEffect(() => {
    setResetOption(() => {
      if (dialogTriggered) {
        setSelectedOption('');
        setDialogTriggered(false);
      }
    });
  }, [setResetOption, dialogTriggered]);

  const validate = () => {
    const newErrors = {};
    if (!checkboxes.age) newErrors.age = 'Bitte bestätige das Alter der Person.';
    if (!checkboxes.inGermany) newErrors.inGermany = 'Bitte bestätige das Engagement der Person in Deutschland.';
    if (!checkboxes.nomineeKnows) newErrors.nomineeKnows = 'Hast du das Einverständnis der Person?';
    if (!selectedOption) newErrors.selectedOption = 'In welchem Bereich engagiert sich die Person?';
    return newErrors;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      next({ selectedOption, ...checkboxes, errors: validationErrors });
    } else {
      next({ selectedOption, ...checkboxes });
    }
  };

  const handleRadioChange = (value) => {
    setSelectedOption(value);
    if (errors.selectedOption) {
      setErrors((prevErrors) => {
        const { selectedOption, ...rest } = prevErrors;
        return rest;
      });
    }
  };

  const handleCheckboxChange = (id) => {
    setCheckboxes((prev) => {
      const newState = { ...prev, [id]: !prev[id] };
      if (errors[id]) {
        setErrors((prevErrors) => {
          const { [id]: removedError, ...rest } = prevErrors;
          return rest;
        });
      }
      return newState;
    });
  };

  return (
    <div className="step text-white flex flex-col gap-6 md:gap-12">
      <div className="flex flex-col gap-3">
        <h2 className="text-2xl font-bold">Nominierungs-Kriterien</h2>
        <p>Bitte bestätige, dass alle Kriterien erfüllt sind, um an der Nominierung teilzunehmen.</p>
      </div>
      <fieldset>
        <legend className="sr-only">Harte Kriterien</legend>
        <div className="space-y-4">
          {[
            { id: 'age', label: 'Ist die engagierte Person zwischen 16 und 24 Jahre alt?', description: "Der Engagementpreis der Allianz Foundation fördert explizit das Engagement junger Menschen." },
            { id: 'inGermany', label: 'Engagiert sich die Person in Deutschland?', description: "Wir suchen junge Menschen, die sich in Deutschland lokal, regional oder bundesweit engagieren. Das Engagement kann online oder offline stattfinden." },
            { id: 'nomineeKnows', label: 'Hast du mit der engagierten Person über die Nominierung gesprochen und sein/ihr Einverständnis?', description: "Wir müssen sichergehen, dass die von dir nominierte Person von der Nominierung weiß. Bei minderjährigen Personen (unter 18) solltest Du mit einem/einer Erziehungsberechtigten gesprochen haben." },
          ].map((checkbox) => (
            <Field key={checkbox.id} className="relative flex items-start gap-4 px-6 py-6 pb-7 bg-white/5 hover:bg-white/10 transition duration-100 cursor-pointer" onClick={() => handleCheckboxChange(checkbox.id)}>
              <div className="min-w-0 flex-1">
                <Label className="text-lg leading-[1.25] font-semibold cursor-pointer">
                  <h3>{checkbox.label}</h3>
                </Label>
                <p id={`${checkbox.id}-description`} className="text-sm text-white/80 mt-1">
                  {checkbox.description}
                </p>
                {errors[checkbox.id] && <p className="error text-orange">{errors[checkbox.id]}</p>}
              </div>
              <div className="mt-1.5 md:mt-4">
                <Checkbox
                  checked={checkboxes[checkbox.id]}
                  onChange={(checked) => handleCheckboxChange(checkbox.id)}
                  className={clsx(
                    "group block size-5 bg-white/50 rounded-sm cursor-pointer",
                    checkboxes[checkbox.id] && "data-[checked]:bg-purple data-[checked]:ring-purple"
                  )}
                >
                  <svg className={clsx("stroke-white size-4 transition relative top-0.5 left-0.5", checkboxes[checkbox.id] ? "opacity-100" : "opacity-0")} strokeWidth={3} viewBox="0 0 24 24" fill="none">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                  </svg>
                </Checkbox>
              </div>
            </Field>
          ))}
          <div className="download ml-5">
            <a href="https://res.cloudinary.com/allianz-foundation/image/upload/v1744365491/NEU_240708_Moversof_Tomorrow_Einverstaendniserklaerung_9639889c71.pdf" target="blank"
              className="flex gap-3 font-semibold text-sm leading-none hover:text-purple">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="size-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Einverständniserklärung für Deine Unterlagen
            </a>
          </div>
        </div>
        <div className="w-full mt-12 md:mt-20">
          <div className="flex flex-col gap-3 mb-6">
            <h2 className="text-xl font-bold">In welchem Bereich engagiert sich die Person?</h2>
          </div>
          <div className="mx-auto w-full">
            {errors.selectedOption && <p className="error text-orange mt-2">{errors.selectedOption}</p>}
            <RadioGroup value={selectedOption} onChange={handleRadioChange} aria-label="Options" className="space-y-4">
              {options.map((option) => (
                <Radio
                  key={option.name}
                  value={option.name}
                  className="group relative flex cursor-pointer bg-white/5 py-6 pb-7 px-6 text-white transition focus:outline-none data-[focus]:outline-1 data-[focus]:outline-white data-[checked]:bg-white/10 hover:bg-white/10"
                >
                  <div className="flex w-full items-center justify-between relative">
                    <div className="text-sm/7">
                      <p className="text-lg font-semibold">{option.subheadline}</p>
                      <p className="text-sm text-white/80 max-w-lg pr-20 mt-1">
                        {option.desc}
                      </p>
                    </div>
                    <div className="absolute right-0 size-6 rounded-full bg-white/50 text-white transition group-data-[checked]:bg-purple mt-1.5">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0  24 24" strokeWidth={3} stroke="currentColor" className="size-4 fill-purple text-white opacity-0 transition group-data-[checked]:opacity-100 relative top-1 left-1">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                    </div>
                  </div>
                </Radio>
              ))}
            </RadioGroup>
          </div>
        </div>
      </fieldset>
      <nav className="progress-nav">
        <div className="inner flex h-full items-center justify-between">
          <button onClick={abort} className="flex gap-3 items-center hover:text-purple">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18" />
            </svg>
            <span className="block -mt-1 font-semibold tracking-wide">Abbrechen</span>
          </button>
          <button onClick={handleSubmit} className="primary-form">
            <span className="font-semibold tracking-wide -mt-0.5 block text-lg">Kriterien prüfen</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default Step1;
